import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchIcon, ShoppingBagIcon, PlusIcon, MinusIcon, XIcon, StoreIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface Shop {
  id: number;
  name: string;
  description: string;
  logo: string;
}
interface Product {
  id: number;
  shopId: number;
  name: string;
  price: number;
  description: string;
  image: string;
  category: string;
}
interface CartItem {
  productId: number;
  quantity: number;
  product: Product;
}
interface ShopProductsPaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onCreateLink?: () => void;
}
const shops: Shop[] = [{
  id: 1,
  name: 'Urban Outfitters',
  description: 'Contemporary clothing and lifestyle products',
  logo: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
}, {
  id: 2,
  name: 'Tech Haven',
  description: 'Latest electronics and gadgets',
  logo: 'https://images.unsplash.com/photo-1550009158-9ebf69173e03?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
}, {
  id: 3,
  name: 'Home Essentials',
  description: 'Quality products for your home',
  logo: 'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
}];
const products: Product[] = [{
  id: 101,
  shopId: 1,
  name: 'Basic T-Shirt',
  price: 599,
  description: 'Comfortable cotton t-shirt',
  image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Clothing'
}, {
  id: 102,
  shopId: 1,
  name: 'Slim Fit Jeans',
  price: 1499,
  description: 'Classic blue denim jeans',
  image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Clothing'
}, {
  id: 103,
  shopId: 1,
  name: 'Leather Wallet',
  price: 899,
  description: 'Genuine leather wallet',
  image: 'https://images.unsplash.com/photo-1627123424574-724758594e93?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Accessories'
}, {
  id: 201,
  shopId: 2,
  name: 'Premium Headphones',
  price: 2699,
  description: 'Noise cancelling headphones',
  image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Electronics'
}, {
  id: 202,
  shopId: 2,
  name: 'Smart Watch',
  price: 5999,
  description: 'Fitness and health tracking',
  image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Electronics'
}, {
  id: 203,
  shopId: 2,
  name: 'Wireless Earbuds',
  price: 3899,
  description: 'True wireless Bluetooth earbuds',
  image: 'https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Electronics'
}, {
  id: 301,
  shopId: 3,
  name: 'Ceramic Coffee Mug',
  price: 389,
  description: 'Handmade ceramic mug',
  image: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Kitchen'
}, {
  id: 302,
  shopId: 3,
  name: 'Decorative Throw Pillow',
  price: 749,
  description: 'Soft decorative pillow',
  image: 'https://images.unsplash.com/photo-1592078615290-033ee584e267?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Home Decor'
}, {
  id: 303,
  shopId: 3,
  name: 'Scented Candle Set',
  price: 1049,
  description: 'Set of 3 scented soy candles',
  image: 'https://images.unsplash.com/photo-1602523069662-0b1ea6503f14?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
  category: 'Home Decor'
}];
const formatPrice = (amount: number) => formatCurrencyAmount(amount, {
  fractionDigits: 0
});
export function ShopProductsPayment({
  onExpiryDateChange,
  onCreateLink
}: ShopProductsPaymentProps) {
  const {
    t
  } = useTranslation('payments');
  const [searchTerm, setSearchTerm] = useState('');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedShopId, setSelectedShopId] = useState<number>(1);
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  const filteredProducts = products.filter(p => p.shopId === selectedShopId && (searchTerm === '' || p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.category.toLowerCase().includes(searchTerm.toLowerCase())));
  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(i => i.productId === product.id);
      if (existing) {
        return prev.map(i => i.productId === product.id ? {
          ...i,
          quantity: i.quantity + 1
        } : i);
      }
      return [...prev, {
        productId: product.id,
        quantity: 1,
        product
      }];
    });
  };
  const removeFromCart = (productId: number) => {
    setCart(prev => prev.filter(i => i.productId !== productId));
  };
  const updateQuantity = (productId: number, qty: number) => {
    if (qty < 1) return;
    setCart(prev => prev.map(i => i.productId === productId ? {
      ...i,
      quantity: qty
    } : i));
  };
  const totalAmount = cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const totalItems = cart.reduce((s, i) => s + i.quantity, 0);
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    onExpiryDateChange?.(date);
  };
  return <div className="flex gap-5 items-start" data-id="element-8088">
      {/* ─── LEFT COLUMN ─── */}
      <div className="flex-1 space-y-5 min-w-0" data-id="element-8089">
        {/* ─── SHOP & PRODUCTS ─── */}
        <div className="bg-white rounded-xl border border-gray-200" data-id="element-8090">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-8091">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8092">
              <StoreIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-8093" />
              {t('components.ShopProductsPayment.shop_products')}
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-id="element-8094">
              {t('components.ShopProductsPayment.select_a_shop_and_add_products')}
            </p>
          </div>

          {/* Shop selection — edge-to-edge rows */}
          <div data-id="element-8095">
            {shops.map((shop, idx) => <div key={shop.id} className={`flex items-center px-6 py-4 cursor-pointer transition-colors ${selectedShopId === shop.id ? 'bg-[#E2231A]/5' : 'hover:bg-gray-50'} ${idx < shops.length - 1 ? 'border-b border-gray-200' : ''}`} onClick={() => setSelectedShopId(shop.id)} data-id="element-8096">
                <input type="radio" name="shop" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0" checked={selectedShopId === shop.id} onChange={() => setSelectedShopId(shop.id)} data-id="element-8097" />

                <div className="ml-4 flex items-center flex-1 min-w-0" data-id="element-8098">
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-8099">
                    <img src={shop.logo} alt={shop.name} className="w-full h-full object-cover" data-id="element-8100" />
                  </div>
                  <div className="ml-3 min-w-0" data-id="element-8101">
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-8102">
                      {shop.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate" data-id="element-8103">
                      {shop.description}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>

          {/* Search */}
          <div className="px-6 py-4 border-t border-gray-200" data-id="element-8104">
            <div className="relative" data-id="element-8105">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8106">
                <SearchIcon size={16} className="text-gray-400" data-id="element-8107" />
              </div>
              <input type="text" className={`${inputClass} pl-10`} placeholder={t('components.ShopProductsPayment.attrs.search_products')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-8108" />
            </div>
          </div>

          {/* Product grid */}
          <div className="px-6 pb-6" data-id="element-8109">
            {filteredProducts.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-8110">
                {filteredProducts.map(product => {
              const inCart = cart.find(i => i.productId === product.id);
              return <div key={product.id} className="border border-gray-200 rounded-lg p-3 flex gap-3" data-id="element-8111">
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-8112">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-8113" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-8114">
                        <div className="flex items-start justify-between gap-2" data-id="element-8115">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-8116">
                            {product.name}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 flex-shrink-0" data-id="element-8117">
                            {formatPrice(product.price)}
                          </p>
                        </div>
                        <p className="text-[11px] text-gray-400 mt-0.5" data-id="element-8118">
                          {product.category}
                        </p>
                        <div className="mt-2" data-id="element-8119">
                          <button type="button" onClick={() => addToCart(product)} className="px-2.5 py-1 bg-[#E2231A]/10 text-[#9F140F] hover:bg-[#E2231A]/20 rounded-md text-xs font-medium flex items-center transition-colors" data-id="element-8120">
                            <PlusIcon size={12} className="mr-1" data-id="element-8121" />
                            {inCart ? `Add (${inCart.quantity})` : 'Add'}
                          </button>
                        </div>
                      </div>
                    </div>;
            })}
              </div> : <div className="text-center py-8" data-id="element-8122">
                <SearchIcon size={20} className="mx-auto text-gray-400 mb-2" data-id="element-8123" />
                <p className="text-sm font-medium text-gray-700" data-id="element-8124">
                  {t('components.ShopProductsPayment.no_products_found')}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-8125">
                  {t('components.ShopProductsPayment.try_a_different_search_term')}
                </p>
              </div>}
          </div>
        </div>

        {/* ─── ADDITIONAL OPTIONS ─── */}
        <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} data-id="element-8126" />
      </div>

      {/* ─── RIGHT COLUMN — ORDER SUMMARY ─── */}
      <div className="w-80 flex-shrink-0 sticky top-6" data-id="element-8127">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8128">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-8129">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8130">
              <ShoppingBagIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-8131" />
              {t('components.ShopProductsPayment.order_summary')}
            </h3>
          </div>

          {cart.length > 0 ? <>
              <div data-id="element-8132">
                {cart.map((item, idx) => <div key={item.productId} className={`px-6 py-3 flex items-center gap-3 ${idx < cart.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-8133">
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-8134">
                      <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" data-id="element-8135" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-8136">
                      <div className="flex items-start justify-between gap-1" data-id="element-8137">
                        <p className="text-sm font-medium text-gray-900 truncate" data-id="element-8138">
                          {item.product.name}
                        </p>
                        <button type="button" onClick={() => removeFromCart(item.productId)} aria-label="Remove from cart" className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0" data-id="element-8139">
                          <XIcon size={14} aria-hidden="true" data-id="element-8140" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-1" data-id="element-8141">
                        <div className="flex items-center border border-gray-200 rounded-md" data-id="element-8142">
                          <button type="button" onClick={() => updateQuantity(item.productId, item.quantity - 1)} className="px-1.5 py-0.5 text-gray-500 hover:text-gray-700 transition-colors" data-id="element-8143">
                            <MinusIcon size={12} data-id="element-8144" />
                          </button>
                          <span className="px-2 text-xs font-medium text-gray-900" data-id="element-8145">
                            {item.quantity}
                          </span>
                          <button type="button" onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="px-1.5 py-0.5 text-gray-500 hover:text-gray-700 transition-colors" data-id="element-8146">
                            <PlusIcon size={12} data-id="element-8147" />
                          </button>
                        </div>
                        <p className="text-xs font-medium text-gray-900" data-id="element-8148">
                          {formatPrice(item.product.price * item.quantity)}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/50" data-id="element-8149">
                <div className="flex items-center justify-between" data-id="element-8150">
                  <span className="text-sm text-gray-500" data-id="element-8151">
                    {t('components.renderSchedulePreview.total')}
                  </span>
                  <span className="text-lg font-bold text-gray-900" data-id="element-8152">
                    {formatPrice(totalAmount)}
                  </span>
                </div>
                <p className="text-[11px] text-gray-400 mt-1" data-id="element-8153">
                  {totalItems} {t('components.ShopProductsPayment.item')}
                  {totalItems !== 1 ? 's' : ''}
                </p>
              </div>
            </> : <div className="px-6 py-10 text-center" data-id="element-8154">
              <ShoppingBagIcon size={20} className="mx-auto text-gray-400 mb-2" data-id="element-8155" />

              <p className="text-sm font-medium text-gray-700" data-id="element-8156">
                {t('components.ProductSelectionPayment.no_products_selected')}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-8157">
                {t('components.ShopProductsPayment.add_products_from_the_shop')}
              </p>
            </div>}
        </div>

        {/* Create link button */}
        <button type="button" disabled={cart.length === 0} onClick={onCreateLink} className={`w-full mt-4 px-6 py-2.5 text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center justify-center ${cart.length > 0 ? 'bg-[#E2231A] hover:bg-[#C41B14] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-8158">
          {t('components.ShopProductsPayment.create_payment_link')}
        </button>
      </div>
    </div>;
}